import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit2,
  Eye,
  Filter,
  Search,
  Trash2,
  Plus,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import CONFIGS from "../../config";
import {
  deleteCardsCategories,
  getCardsCategoriesList,
} from "../../services/Admin/cardsCategoriesService";
import { notify } from "../../components/Common/Alerts";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import TooltipWrapper from "../../components/Common/TooltipWrapper";
import ConfirmModal from "../../components/Common/ConfirmModal";
import CommonSkeleton from "../../components/Common/CommonSkeleton";

const CardCategories = () => {
  const navigate = useNavigate();

  const [params, setParams] = useState({
    limit: CONFIGS.DATA_LIMIT,
    page: 1,
    search: "",
    is_reload: true,
  });

  const [cardsList, setCardList] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [deleteShow, setDeleteShow] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [btnDeleteLoading, setBtnDeleteLoading] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState("list");
  const currentPage = params.page;
  const totalPages = Math.ceil(totalRecord / params.limit);
  const startIndex = (currentPage - 1) * params.limit;
  const endIndex = startIndex + params.limit;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchCardList = useCallback(() => {
    if (params.page === 1) {
      setLoading(true);
    } else {
      setLoadingPage(true);
    }

    getCardsCategoriesList(params)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const newData = decrypted?.data || [];

        setCardList((prev) =>
          params.is_reload ? newData : [...prev, ...newData],
        );

        setTotalRecord(decrypted?.total || 0);
        setHasMore(newData.length >= params.limit);
      })
      .catch(() => {
        setCardList([]);
      })
      .finally(() => {
        setLoading(false);
        setLoadingPage(false);
      });
  }, [params]);

  useEffect(() => {
    fetchCardList();
  }, [params]);

  useEffect(() => {
    setHasMore(true);
    setParams((prev) => ({
      ...prev,
      page: 1,
      is_reload: true,
    }));
  }, [isMobile]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isBottom = scrollTop + clientHeight >= scrollHeight - 50;
    if (isBottom && hasMore && !loadingPage) {
      setLoadingPage(true);

      setParams((prev) => ({
        ...prev,
        page: prev.page + 1,
        is_reload: false,
      }));
    }
  };

  const handleDeleteCard = (e) => {
    e.preventDefault();
    setBtnDeleteLoading(true);
    deleteCardsCategories({ uuid: deleteId.uuid })
      .then((response) => {
        let decrypt = decryptData(response.data.data);
        notify.success(decrypt.message);
        setCardList((prev) =>
          prev.filter((item) => item.uuid !== deleteId.uuid),
        );
        setDeleteShow(false);
      })
      .catch((err) => notify.error(Utils.getErrorMessage(err)))
      .finally(() => setBtnDeleteLoading(false));
  };

  const handleParams = (page) => setParams((prev) => ({ ...prev, page }));

  const handleSearch = () => {
    setParams((prev) => ({
      ...prev,
      ...filters,
      page: 1,
      is_reload: true,
    }));
  };

  const handleReset = () => {
    setFilters({
      search: "",
    });
    setParams({
      limit: 10,
      page: 1,
      search: "",
      is_reload: true,
    });
    setHasMore(true);
    setLoadingPage(false);
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0 transition-colors duration-300">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 dark:text-white tracking-tight">
            Card Categories
          </h1>
        </div>
        <button
          type="button"
          onClick={() => navigate("/card-categories-add")}
          className="w-full md:w-auto bg-[#D4AF37] text-white px-6 py-3 md:py-3.5 rounded-xl font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-orange-200 dark:shadow-none hover:scale-[1.02] transition-all active:scale-95"
        >
          <Plus size={18} /> Add Card Categories
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 w-full">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400 dark:text-slate-500">
              <Search size={18} />
            </span>
            <input
              type="text"
              value={filters?.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              placeholder="Search by name..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50/60 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700/60 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 transition-all"
            />
          </div>

          <div className="flex items-center justify-between md:justify-end gap-3 sm:gap-4 shrink-0 w-full md:w-auto">
            <div className="hidden md:flex flex items-center bg-slate-100 dark:bg-slate-900 rounded-xl p-1">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === "list"
                    ? "bg-white dark:bg-slate-800 shadow-sm text-amber-500 font-bold"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                }`}
              >
                <List size={16} />
              </button>

              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === "grid"
                    ? "bg-white dark:bg-slate-800 shadow-sm text-amber-500 font-bold"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                }`}
              >
                <LayoutGrid size={16} />
              </button>
            </div>

            {/* Clear & Filter Button Pair Group */}
            <div className="flex items-center gap-2 ml-auto md:ml-0">
              <button
                type="button"
                onClick={handleReset}
                className="text-xs font-black text-slate-400 dark:text-slate-500 px-3 py-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-900/40 hover:text-orange-500 transition-colors"
              >
                Clear
              </button>

              <button
                type="button"
                onClick={handleSearch}
                className="border border-orange-200 dark:border-orange-900/50 bg-white dark:bg-slate-800 text-orange-500 rounded-xl px-4 py-2 text-xs font-black flex items-center gap-2 hover:bg-orange-50 dark:hover:bg-orange-900/20 active:scale-98 transition-all shadow-xs"
              >
                <Filter size={14} />
                <span>Filter</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl md:rounded-[2.5rem] border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden p-3 md:p-6">
        {viewMode === "list" && (
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-4 w-12">#</th>
                  <th className="px-6 py-4">Card Name</th>
                  <th className="px-6 py-4">Categories</th>
                  <th className="px-6 py-4">Multiplier</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              {loading ? (
                <CommonSkeleton type="table" count={6} />
              ) : (
                <tbody className="text-sm font-bold text-slate-600 dark:text-slate-300">
                  {cardsList?.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50/80 dark:hover:bg-slate-900/30 transition-colors"
                    >
                      <td className="px-6 py-4 text-slate-400 dark:text-slate-600">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-6 py-4 font-black text-slate-800 dark:text-slate-100">
                        {item.card_name}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-700 rounded-md text-xs font-semibold">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-orange-600 dark:text-orange-400 font-black">
                        {item.multiplier}x
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center gap-2">
                          <TooltipWrapper title="Edit" placement="top">
                            <button
                              onClick={() =>
                                navigate(`/card-categories-update/${item.uuid}`)
                              }
                              className="p-2 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-400 dark:text-slate-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-500 transition-all"
                            >
                              <Edit2 size={16} />
                            </button>
                          </TooltipWrapper>
                          <TooltipWrapper title="Delete" placement="top">
                            <button
                              onClick={() => {
                                setDeleteId(item);
                                setDeleteShow(true);
                              }}
                              className="p-2 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-400 dark:text-slate-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 transition-all"
                            >
                              <Trash2 size={16} />
                            </button>
                          </TooltipWrapper>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        )}
        {(viewMode === "grid" || viewMode === "list") && (
          <div
            className={`${viewMode === "list" ? "block md:hidden" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"} gap-4`}
          >
            {loading ? (
              <CommonSkeleton
                type="card"
                count={6}
                columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
              />
            ) : cardsList?.length > 0 ? (
              cardsList?.map((item) => (
                <div
                  key={item.id}
                  className="p-4 border border-slate-100 dark:border-slate-700 rounded-2xl bg-slate-50/50 dark:bg-slate-900/20 flex flex-col justify-between space-y-4 shadow-2xs"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 bg-gradient-to-tr from-blue-600 to-indigo-700 rounded-xl shrink-0 shadow-xs flex items-center justify-center text-white font-black text-xs">
                        Card
                      </div>
                      <div>
                        <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 leading-tight break-words line-clamp-2">
                          {item.card_name}
                        </h3>
                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase mt-1 tracking-wider">
                          {item.category}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-slate-100 dark:border-slate-700">
                    <div className="flex flex-col">
                      <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        Multiplier
                      </span>
                      <span className="text-base font-black text-orange-600 dark:text-orange-400">
                        {item.multiplier}x
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          navigate(`/card-categories-update/${item.uuid}`)
                        }
                        className="p-2 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-400 dark:text-slate-500 active:bg-orange-50 dark:active:bg-orange-900/20 active:text-orange-500 transition-colors"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => {
                          setDeleteId(item);
                          setDeleteShow(true);
                        }}
                        className="p-2 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-400 dark:text-slate-500 active:bg-red-50 dark:active:bg-red-900/20 active:text-red-500 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full py-20 flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 rounded-3xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                  <Search
                    size={34}
                    className="text-orange-500 dark:text-orange-400"
                  />
                </div>

                <h2 className="mt-5 text-xl font-black text-slate-800 dark:text-white">
                  No Card Categories Found
                </h2>

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  Try adjusting your filters
                </p>
              </div>
            )}
          </div>
        )}

        {cardsList?.length > 0 && (
          <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 font-medium">
              Showing{" "}
              <span className="font-bold text-slate-700 dark:text-slate-200">
                {startIndex + 1}
              </span>{" "}
              to{" "}
              <span className="font-bold text-slate-700 dark:text-slate-200">
                {Math.min(endIndex, totalRecord)}
              </span>{" "}
              of {totalRecord}
            </p>

            {/* Navigation Controls */}
            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => handleParams(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:pointer-events-none transition-all"
              >
                <ChevronLeft size={16} />
              </button>

              {/* Desktop Numeric Pages */}
              <div className="hidden sm:flex gap-1.5">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => handleParams(page)}
                      className={`w-9 h-9 rounded-xl text-xs md:text-sm font-black transition-all ${
                        currentPage === page
                          ? "bg-amber-500 text-white shadow-xs"
                          : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>

              {/* Mobile Explicit Page Indicator */}
              <div className="sm:hidden px-3">
                <span className="text-xs font-black text-amber-600 dark:text-amber-400">
                  Page {currentPage} of {totalPages}
                </span>
              </div>

              {/* Next Button */}
              <button
                onClick={() => handleParams(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:pointer-events-none transition-all"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      <ConfirmModal
        open={deleteShow}
        onClose={() => setDeleteShow(false)}
        onConfirm={handleDeleteCard}
        loading={btnDeleteLoading}
        titleKey={"Delete Card Categories"}
        descriptionKey={`Are you sure you want to remove ${deleteId?.full_name || "this"}?`}
        confirmText="Yes, Delete"
        icon={<Trash2 size={40} className="text-red-500" />}
      />
    </div>
  );
};

export default CardCategories;
